import { autoinject, bindable, bindingMode } from "aurelia-framework";
import { AdministratorSimple } from "company-dashboard/services/identity";
import { ToastService } from "shared/framework";
import { BaseModule } from "shared/framework/components/base-module/base-module";
import { Operation } from "shared/utilities";
import { ModalService } from "shared/framework";
import { AddAdministrator } from "./modals/add-administrator/add-administrator";
import { AgreementDocument, CompanyDetailed, CompanyService } from "company-dashboard/model/dashboard/company";
import { AgreementDocumentService } from "company-dashboard/model/dashboard/company/services/agreement-document-service";

/**
 * Represents the module.
 */
@autoinject
export class CompanyModule extends BaseModule
{
	/**
	 * Creates a new instance of the type.
	 */
	public constructor(companyService: CompanyService, agreementDocumentService: AgreementDocumentService, toastService: ToastService, modalService: ModalService)
	{
		super();

		this._companyService = companyService;
		this._agreementDocumentService = agreementDocumentService;
		this._toastService = toastService;
		this._modalService = modalService;

		this._contructed = true;
	}

	private readonly _companyService: CompanyService;
	private readonly _agreementDocumentService: AgreementDocumentService;
	private readonly _toastService: ToastService;
	private readonly _modalService: ModalService;
	private readonly _contructed;

	protected failed: boolean = false;

	@bindable({ defaultBindingMode: bindingMode.toView })
	protected updatePage: boolean;

	/**
	 * The items to represent in the table
	 */
	protected company: CompanyDetailed | undefined;

	/**
	 * The items to represent in the list of agreement documents
	 */
	protected agreementDocuments: AgreementDocument[] | undefined;

	/**
	 * The items to represent in the list of administrators
	 */
	protected administrators: AdministratorSimple[] | undefined;

	/**
	 * The most recent update operation
	 */
	protected updateOperation: Operation;

	protected updatePageChanged(newValue: boolean): void
	{
		if (newValue)
		{
			this.update();
			this.updatePage = false;
		}
	}

	protected async addAdministrator(): Promise<void>
	{
		this.updatePage = await this._modalService.open(AddAdministrator).promise;
	}

	protected async readDocument(s3Key: string): Promise<void>
	{
		let blob = await this._agreementDocumentService.getAgreementDocument(s3Key);

		let anchor = document.createElement("a");
		anchor.href = URL.createObjectURL(blob);
		anchor.download = s3Key;
		document.body.appendChild(anchor);
		anchor.click();
		document.body.removeChild(anchor);
		URL.revokeObjectURL(anchor.href);
	}

	/**
	 * Called by the framework when the module is activated.
	 * @returns A promise that will be resolved, when the module is activated.
	 */
	public doActivate(): void
	{
		this.update();
	}

	/**
	 * Updates the page by fetching the latest data.
	 */
	protected update(): void
	{
		// Returns if the object is not contructed.
		// This is needed because the `observable` decorator called the change handler when the
		// initial property value is set, which happens before the constructor is called
		if (!this._contructed)
		{
			return;
		}

		if (this.updateOperation != null)
		{
			this.updateOperation.abort();
		}

		// Create and execute the operation
		this.updateOperation = new Operation(async signal =>
		{
			this.failed = false;

			try
			{
				// Fetch the data
				const result = await this._companyService.getCompanyInformations(signal);

				this.administrators = result.administrators;
				this.company = result.companyInformation;
				this.agreementDocuments = result.agreementDocuments;
			}
			catch (error: any)
			{
				this.failed = true;
				this._toastService.open(
					"error",
					{
						"message": error.message
					}
				)
			}
		})
	}
}
